// CR075-P1: Unified Staging Deals View with attempt-scoped filtering and evidence links
// CR080: Removed FlyerPipelineActionsPanel - now lives in Steps tab as FlyerPipelineStepsPanel
// CR094: Separated deal items from flyer artifacts; AI deals show PENDING_REVIEW status
// CR: Added validated payload unwrapping utilities for proper field access
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  RefreshCw, Search, ExternalLink, FileText, Globe, Eye, ChevronLeft, ChevronRight,
  Image, FileJson, Link2, ImageIcon, CheckCircle2, Loader2, AlertCircle, ChevronDown, Layers
} from "lucide-react";
import { VariantsBadge, VariantsDetailList } from "./VariantsBadge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { FlyerRenderDialog } from "./FlyerRenderDialog";
import { 
  getValidatedDeal, 
  getValidationMeta, 
  getDealType,
  getHeadlineMechanic,
  getPurchaseBrand,
  getVariants,
  getDates,
  getValue,
  getConfidence,
  // CR226 Scope C: Fallback helpers with evidence_json support
  getHeadlineWithFallback,
  getDatesWithFallback,
  getValueWithFallback,
  getDealTypeWithFallback,
  getVariantsWithFallback,
  type ExtractedDeal,
  type ValidationMeta
} from "@/lib/batch-runs/validatedPayloadUtils";

interface StagingDealItem {
  id: string;
  batch_run_id: string;
  source_type: string;
  merchant_key: string;
  merchant_source_id: string | null;
  source_url_used: string | null;
  deal_link: string | null;
  detail_link: string | null;
  
  // CR090: Normalized columns for display
  merchant_name: string | null;
  deal_type: string | null;
  offer_text: string | null;
  confidence: number | null;
  purchase_amount: number | null;
  purchase_brand: string | null;
  reward_type: string | null;
  reward_amount: number | null;
  points_amount: number | null;
  points_program: string | null;
  reward_merchant: string | null;
  validity_start_date: string | null;
  validity_end_date: string | null;
  validity_raw_text: string | null;
  extraction_method: string | null;
  
  // CR: Raw validated_payload_json - use getValidatedDeal() to access deal fields
  validated_payload_json: unknown;
  evidence_json: {
    flyer_file_id?: string;
    flyer_page_numbers?: number[];
    listing_html_artifact_id?: string;
    listing_snippet?: string;
    detail_html_artifact_id?: string;
    detail_snippet?: string;
    validate_output_artifact_id?: string;
    poc_run_id?: string;
    page_image_url?: string;
    source_flyer_filename?: string;
    page_number?: number;
    // Render manifest fields
    flyer_storage_path?: string;
    flyer_filename?: string;
    render_status?: string;
    flyer_pages?: Array<{ page_number: number; storage_path: string; width: number; height: number }>;
    page_count?: number;
  };
  provenance_json: {
    merchant_source_id?: string;
    prompt_pack_id?: string;
    prompt_pack_snapshot_id?: string;
    execution_order?: number;
    model_ids?: string[];
    flyer_file_id?: string;
    source_type?: string;
    extraction_method?: string; // CR093: "AI_DEEP_SCAN" for AI-extracted deals
  };
  staging_item_key: string;
  created_at: string;
}

interface StagingDealsViewProps {
  batchRunId: string;
  sourceFilter?: string;
  sourcesEnabled?: string[]; // CR080: For showing flyer pipeline actions
}

interface AttemptOption {
  id: string;
  created_at: string;
  status: string;
  records_out: number | null;
  step_key?: string; // CR075: Track source step (S1 Flyer, S2 Website)
}

const PAGE_SIZE = 50;

// CR111: Global stats interface for pagination-independent counts
interface GlobalStats {
  totalDeals: number;
  aiDeals: number;
  websiteDeals: number;
  flyerArtifacts: number;
}

export function StagingDealsView({ batchRunId, sourceFilter: initialSourceFilter, sourcesEnabled = [] }: StagingDealsViewProps) {
  const { toast } = useToast();
  const [items, setItems] = useState<StagingDealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>(initialSourceFilter || "all");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState<StagingDealItem | null>(null);
  const [detailTab, setDetailTab] = useState<string>("deal");
  
  // CR111: Global stats (pagination-independent)
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    totalDeals: 0,
    aiDeals: 0,
    websiteDeals: 0,
    flyerArtifacts: 0,
  });
  
  // CR075: Attempt-scoped filtering
  const [attempts, setAttempts] = useState<AttemptOption[]>([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string>("latest");
  
  // CR075: Flyer render dialog state
  const [renderDialogOpen, setRenderDialogOpen] = useState(false);
  const [renderItem, setRenderItem] = useState<StagingDealItem | null>(null);

  // Fetch available attempts for this batch run (S1 Flyers + S2 Website + S1_AI/S1_OCR)
  const fetchAttempts = async () => {
    const { data } = await supabase
      .from("cr075_batch_run_step_attempts")
      .select("id, created_at, status, records_out, step_key")
      .eq("batch_run_id", batchRunId)
      .in("step_key", ["S1", "S2", "S1_AI", "S1_OCR"]) // CR476: Include truth-path step keys
      .is("merchant_source_id", null) // Only step-level attempts (not per-merchant)
      .order("created_at", { ascending: false });
    
    setAttempts((data || []) as (AttemptOption & { step_key?: string })[]);
    // CR476: Always default to "all" when in initial "latest" state
    // This ensures staging rows are shown even when no matching attempts exist
    if (selectedAttemptId === "latest") {
      setSelectedAttemptId("all");
    }
  };

  useEffect(() => {
    fetchAttempts();
  }, [batchRunId]);

  // CR075-AC: Helper to check if attempt ID is a valid UUID (not a placeholder)
  const isValidAttemptId = (id: string | undefined): boolean => {
    if (!id || id === "latest" || id === "all") return false;
    // Basic UUID format check
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
  };

  // CR111: Fetch global stats (pagination-independent counts)
  const fetchGlobalStats = async () => {
    if (!isValidAttemptId(selectedAttemptId) && selectedAttemptId !== "all") {
      return;
    }
    
    try {
      // Build base query conditions (same as fetchItems but without pagination)
      const baseConditions = {
        batch_run_id: batchRunId,
        ...(selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId) 
          ? { source_attempt_id: selectedAttemptId } 
          : {}),
      };
      
      // Total count query with filters
      let totalQuery = supabase
        .from("cr075_staging_deal_items")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId);
      
      if (selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId)) {
        totalQuery = totalQuery.eq("source_attempt_id", selectedAttemptId);
      }
      if (search) {
        totalQuery = totalQuery.or(`merchant_key.ilike.%${search}%,staging_item_key.ilike.%${search}%`);
      }
      
      // Website count
      let websiteQuery = supabase
        .from("cr075_staging_deal_items")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "WEBSITE_PROMPT_PACK");
      
      if (selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId)) {
        websiteQuery = websiteQuery.eq("source_attempt_id", selectedAttemptId);
      }
      if (search) {
        websiteQuery = websiteQuery.or(`merchant_key.ilike.%${search}%,staging_item_key.ilike.%${search}%`);
      }
      
      // AI deals count (extraction_method = AI_DEEP_SCAN or legacy)
      let aiQuery = supabase
        .from("cr075_staging_deal_items")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .or("extraction_method.eq.AI_DEEP_SCAN,provenance_json->extraction_method.eq.AI_DEEP_SCAN,source_type.eq.FLYER_AI_DEAL");
      
      if (selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId)) {
        aiQuery = aiQuery.eq("source_attempt_id", selectedAttemptId);
      }
      if (search) {
        aiQuery = aiQuery.or(`merchant_key.ilike.%${search}%,staging_item_key.ilike.%${search}%`);
      }
      
      // Flyer artifacts count (FLYER source without AI extraction)
      let artifactsQuery = supabase
        .from("cr075_staging_deal_items")
        .select("id, deal_type, extraction_method, source_type", { count: "exact" })
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "FLYER")
        .is("extraction_method", null);
      
      if (selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId)) {
        artifactsQuery = artifactsQuery.eq("source_attempt_id", selectedAttemptId);
      }
      if (search) {
        artifactsQuery = artifactsQuery.or(`merchant_key.ilike.%${search}%,staging_item_key.ilike.%${search}%`);
      }
      
      // Run all queries in parallel
      const [totalResult, websiteResult, aiResult, artifactsResult] = await Promise.all([
        totalQuery,
        websiteQuery,
        aiQuery,
        artifactsQuery,
      ]);
      
      // Filter artifacts to exclude AI-extracted items (client-side for complex logic)
      const artifactItems = artifactsResult.data || [];
      const flyerArtifactCount = artifactItems.filter(item => {
        const dealType = item.deal_type;
        return dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING" || !dealType;
      }).length;
      
      // Calculate actual deals (total minus artifacts)
      const totalAll = totalResult.count || 0;
      const websiteTotal = websiteResult.count || 0;
      const aiTotal = aiResult.count || 0;
      
      // Deal count = total - artifacts (deals are items with actual deal data)
      const dealCount = totalAll - flyerArtifactCount;
      
      setGlobalStats({
        totalDeals: Math.max(0, dealCount),
        aiDeals: aiTotal,
        websiteDeals: websiteTotal,
        flyerArtifacts: flyerArtifactCount,
      });
    } catch (err) {
      console.error("Failed to fetch global stats:", err);
    }
  };

  const fetchItems = async () => {
    // CR075-AC: Don't fetch until we have a valid attempt ID (not "latest" placeholder)
    // This prevents "Failed to load staging deals" error from invalid query
    if (!isValidAttemptId(selectedAttemptId) && selectedAttemptId !== "all") {
      // Wait for fetchAttempts to resolve the actual attempt ID
      return;
    }
    
    setLoading(true);
    try {
      let query = supabase
        .from("cr075_staging_deal_items")
        .select("*", { count: "exact" })
        .eq("batch_run_id", batchRunId)
        .order("created_at", { ascending: false });

      // CR075: Filter by selected attempt when a valid UUID is selected
      // "all" shows all attempts; valid UUID filters to that attempt only
      if (selectedAttemptId !== "all" && isValidAttemptId(selectedAttemptId)) {
        query = query.eq("source_attempt_id", selectedAttemptId);
      }

      // CR093: Handle AI_DEALS filter via provenance_json, not source_type
      if (sourceFilter !== "all" && sourceFilter !== "AI_DEALS") {
        query = query.eq("source_type", sourceFilter);
      }
      
      // CR090: AI_DEALS filter - use normalized extraction_method column (with fallback)
      if (sourceFilter === "AI_DEALS") {
        // Use OR filter: normalized column OR legacy provenance OR legacy source_type
        query = query.or("extraction_method.eq.AI_DEEP_SCAN,provenance_json->extraction_method.eq.AI_DEEP_SCAN,source_type.eq.FLYER_AI_DEAL");
      }

      if (search) {
        query = query.or(`merchant_key.ilike.%${search}%,staging_item_key.ilike.%${search}%`);
      }

      query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      setItems((data || []) as StagingDealItem[]);
      setTotalCount(count || 0);
    } catch (err) {
      console.error("Failed to fetch staging items:", err);
      toast({
        title: "Error",
        description: "Failed to load staging deals",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CR075-AC: Only fetch when we have a valid attempt ID (UUID) or "all"
    if (isValidAttemptId(selectedAttemptId) || selectedAttemptId === "all") {
      fetchItems();
      fetchGlobalStats(); // CR111: Fetch global stats alongside items
    }
  }, [batchRunId, sourceFilter, page, selectedAttemptId]);

  // CR111: Re-fetch global stats when search changes
  useEffect(() => {
    if (isValidAttemptId(selectedAttemptId) || selectedAttemptId === "all") {
      fetchGlobalStats();
    }
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [search, sourceFilter, selectedAttemptId]);

  const handleSearch = () => {
    setPage(0);
    fetchItems();
    fetchGlobalStats(); // CR111: Update global stats on search
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // CR093: Check if item is from AI deep scan via normalized column OR provenance OR legacy source_type
  const isAiExtractedDeal = (item: StagingDealItem): boolean => {
    return item.extraction_method === "AI_DEEP_SCAN" ||
           item.provenance_json?.extraction_method === "AI_DEEP_SCAN" || 
           item.source_type === "FLYER_AI_DEAL"; // Backward compat for legacy records
  };

  // CR094: Helper to detect flyer artifacts (not actual deals) - these are staging placeholder rows
  const isFlyerArtifact = (item: StagingDealItem): boolean => {
    // CR: Use unwrap helper instead of direct access
    const dealType = item.deal_type || getDealType(item.validated_payload_json);
    // Flyer artifacts are rows with FLYER source_type that are pending render/OCR (not yet AI-extracted)
    return item.source_type === "FLYER" && 
           !isAiExtractedDeal(item) &&
           (dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING" || !dealType);
  };

  const getSourceIcon = (item: StagingDealItem) => {
    if (isAiExtractedDeal(item)) {
      return <Eye className="h-4 w-4" />;
    }
    switch (item.source_type) {
      case "FLYER":
        return <FileText className="h-4 w-4" />;
      case "WEBSITE_PROMPT_PACK":
        return <Globe className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // CR093: Updated to use item instead of just source_type to detect AI extraction
  const getSourceBadge = (item: StagingDealItem) => {
    if (isAiExtractedDeal(item)) {
      return <Badge variant="outline" className="gap-1 bg-purple-50 text-purple-700"><Eye className="h-3 w-3" />AI Deal</Badge>;
    }
    switch (item.source_type) {
      case "FLYER":
        return <Badge variant="outline" className="gap-1"><FileText className="h-3 w-3" />Flyer</Badge>;
      case "WEBSITE_PROMPT_PACK":
        return <Badge variant="outline" className="gap-1"><Globe className="h-3 w-3" />Website</Badge>;
      default:
        return <Badge variant="outline">{item.source_type}</Badge>;
    }
  };

  const getConfidenceBadge = (confidence?: number) => {
    if (!confidence) return <Badge variant="outline">Unknown</Badge>;
    if (confidence >= 0.8) return <Badge className="bg-green-500">High ({(confidence * 100).toFixed(0)}%)</Badge>;
    if (confidence >= 0.6) return <Badge className="bg-yellow-500">Medium ({(confidence * 100).toFixed(0)}%)</Badge>;
    return <Badge variant="destructive">Low ({(confidence * 100).toFixed(0)}%)</Badge>;
  };

  // CR075: Get extraction status for flyer items - enhanced with render status
  const getFlyerRenderStatus = (item: StagingDealItem): {
    status: "pending_render" | "rendered" | "extracted" | "needs_review" | "pending_ocr";
    pageCount?: number;
    canRender: boolean;
  } => {
    // CR: Use unwrap helpers instead of direct access
    const deal = getValidatedDeal(item.validated_payload_json);
    const validationMeta = getValidationMeta(item.validated_payload_json);
    const dealType = deal.deal_type;
    const isPublishable = validationMeta.is_publishable;
    const evidence = item.evidence_json;
    
    // Pending states that need rendering
    const isPendingRender = dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING";
    
    // Check if rendered (has page images ready)
    if (evidence?.render_status === "RENDERED" && (evidence?.flyer_pages?.length ?? 0) > 0) {
      return { 
        status: "rendered", 
        pageCount: evidence.flyer_pages?.length ?? evidence.page_count ?? 0,
        canRender: false,
      };
    }
    
    // Has storage path AND is in pending state = can render
    if (evidence?.flyer_storage_path && isPendingRender) {
      return { status: "pending_render", canRender: true };
    }
    
    // If already extracted (not pending), show as extracted
    if (dealType && !isPendingRender) {
      return { status: "extracted", canRender: false };
    }
    
    // Legacy pending OCR (no storage path)
    if (isPendingRender && !evidence?.flyer_storage_path) {
      return { status: "pending_ocr", canRender: false };
    }
    
    if (isPublishable === false) {
      return { status: "needs_review", canRender: false };
    }
    
    return { status: "pending_ocr", canRender: false };
  };
  
  const getExtractionStatusBadge = (item: StagingDealItem) => {
    // CR094: AI-extracted deals should show PENDING_REVIEW, not OCR-related statuses
    if (isAiExtractedDeal(item)) {
      // CR: Use unwrap helper
      const validationMeta = getValidationMeta(item.validated_payload_json);
      const isPublishable = validationMeta.is_publishable;
      if (isPublishable === false) {
        return (
          <Badge variant="outline" className="bg-orange-100 text-orange-800 gap-1">
            ⚠️ Needs Review
          </Badge>
        );
      }
      return (
        <Badge variant="outline" className="bg-indigo-100 text-indigo-800 gap-1">
          <Eye className="h-3 w-3" />
          Pending Review
        </Badge>
      );
    }
    
    const renderStatus = getFlyerRenderStatus(item);
    
    switch (renderStatus.status) {
      case "extracted":
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Extracted
          </Badge>
        );
      case "rendered":
        return (
          <Badge variant="outline" className="bg-blue-100 text-blue-800 gap-1">
            <ImageIcon className="h-3 w-3" />
            Rendered ({renderStatus.pageCount} pages)
          </Badge>
        );
      case "pending_render":
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 gap-1">
            <AlertCircle className="h-3 w-3" />
            Pending Render
          </Badge>
        );
      case "needs_review":
        return (
          <Badge variant="outline" className="bg-orange-100 text-orange-800 gap-1">
            ⚠️ Needs Review
          </Badge>
        );
      case "pending_ocr":
      default:
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 gap-1">
            <Loader2 className="h-3 w-3" />
            Pending OCR
          </Badge>
        );
    }
  };

  // CR488: Detect gift card reward signals from staging item evidence
  const hasGiftCardSignals = (item: StagingDealItem): boolean => {
    const offerText = item.offer_text || "";
    const evidence = item.evidence_json as Record<string, unknown> || {};
    const rawDeal = (evidence.raw_deal || {}) as Record<string, unknown>;
    const rawDealValue = ((rawDeal?.value || {}) as Record<string, unknown>);
    const rawDealBrands = ((rawDeal?.brands || {}) as Record<string, unknown>);
    const headlineMechanic = (rawDeal?.headline_mechanic as string) || "";
    const rewardText = (rawDealValue?.reward_text as string) || "";
    
    const textToCheck = `${offerText} ${headlineMechanic} ${rewardText}`;
    return /gift\s*card/i.test(textToCheck) || !!rawDealBrands?.bonus_brand;
  };

  // CR090+CR226: Value display with evidence_json fallback
  const getValueDisplay = (item: StagingDealItem): string => {
    // CR226: Get effective deal type with fallback
    const effectiveDealType = getDealTypeWithFallback(
      item.deal_type, 
      item.validated_payload_json, 
      item.evidence_json
    );
    
    // POINTS deal: Spend $X → Y Program
    if (effectiveDealType === "POINTS") {
      const parts: string[] = [];
      if (item.purchase_amount) parts.push(`Spend $${item.purchase_amount}`);
      if (item.points_amount && item.points_program) {
        parts.push(`${item.points_amount} ${item.points_program}`);
      } else if (item.points_amount) {
        parts.push(`${item.points_amount} pts`);
      }
      return parts.length > 0 ? parts.join(" → ") : "-";
    }
    
    // CR488: When gift card signals exist and deal is DISCOUNT, 
    // never show purchase_amount - reward_amount math ("$100 for $85")
    if (effectiveDealType === "DISCOUNT" && hasGiftCardSignals(item)) {
      // Show bonus-style value instead of discount subtraction
      if (item.reward_amount) {
        return `+$${item.reward_amount} bonus`;
      }
      if (item.purchase_amount) {
        return `Spend $${item.purchase_amount}`;
      }
      // Fall through to evidence fallback
    }
    
    // Reward amount with type label (from scalar columns)
    if (item.reward_amount) {
      const label = item.reward_type === "BONUS_CARD" ? "bonus" : 
                    item.reward_type === "DISCOUNT" ? "off" : "";
      return `$${item.reward_amount}${label ? ` ${label}` : ""}`;
    }
    
    // Just purchase amount (from scalar columns)
    if (item.purchase_amount) {
      return `Spend $${item.purchase_amount}`;
    }
    
    // CR226 Scope C: Use fallback helper with evidence_json support
    const value = getValueWithFallback(
      item.purchase_amount, 
      item.reward_amount, 
      item.validated_payload_json, 
      item.evidence_json
    );
    
    if (!value.bonus_value_cad && !value.percent_off && !value.qualifying_spend_cad) {
      return "-";
    }
    
    const parts: string[] = [];
    if (value.bonus_value_cad) parts.push(`+$${value.bonus_value_cad}`);
    if (value.percent_off) parts.push(`${value.percent_off}% off`);
    if (value.qualifying_spend_cad) parts.push(`min $${value.qualifying_spend_cad}`);
    return parts.length > 0 ? parts.join(", ") : "-";
  };
  
  // CR090: Get source info with secondary text
  const getSourceInfo = (item: StagingDealItem): { primary: string; secondary: string } => {
    if (item.source_type === "FLYER" || isAiExtractedDeal(item)) {
      const filename = item.evidence_json?.source_flyer_filename || item.evidence_json?.flyer_filename || "flyer";
      const pageNum = item.evidence_json?.page_number;
      return {
        primary: "Flyer",
        secondary: pageNum ? `${filename} p.${pageNum}` : filename
      };
    }
    if (item.source_type === "WEBSITE_PROMPT_PACK") {
      const url = item.source_url_used;
      try {
        const domain = url ? new URL(url).hostname : "";
        return { primary: "Website", secondary: domain };
      } catch {
        return { primary: "Website", secondary: url?.slice(0, 30) || "" };
      }
    }
    return { primary: item.source_type, secondary: "" };
  };

  const hasEvidence = (item: StagingDealItem) => {
    const e = item.evidence_json;
    return !!(
      e.flyer_file_id ||
      e.listing_html_artifact_id ||
      e.listing_snippet ||
      e.detail_html_artifact_id ||
      e.detail_snippet ||
      e.validate_output_artifact_id ||
      e.page_image_url ||
      e.flyer_pages
    );
  };
  
  // CR075: Count pending render flyers for bulk action
  const pendingRenderFlyers = useMemo(() => 
    items.filter(i => i.source_type === "FLYER" && getFlyerRenderStatus(i).canRender),
    [items]
  );
  
  // CR075: Render stats
  const flyerStats = useMemo(() => {
    const flyers = items.filter(i => i.source_type === "FLYER");
    return {
      total: flyers.length,
      pendingRender: flyers.filter(i => getFlyerRenderStatus(i).status === "pending_render").length,
      rendered: flyers.filter(i => getFlyerRenderStatus(i).status === "rendered").length,
      extracted: flyers.filter(i => getFlyerRenderStatus(i).status === "extracted").length,
    };
  }, [items]);
  
  // CR080: Compute flyer pipeline stats for actions panel
  const flyerPipelineStats = useMemo(() => {
    const flyers = items.filter(i => i.source_type === "FLYER");
    let ocrComplete = 0;
    let candidatesFound = 0;
    let aiComplete = 0;
    
    for (const flyer of flyers) {
      const evidence = flyer.evidence_json as Record<string, unknown>;
      // Check OCR status
      if (evidence?.ocr_pages && Array.isArray(evidence.ocr_pages) && (evidence.ocr_pages as unknown[]).length > 0) {
        ocrComplete++;
      }
      // Count candidate pages
      if (evidence?.candidate_pages && Array.isArray(evidence.candidate_pages)) {
        candidatesFound += (evidence.candidate_pages as unknown[]).length;
      }
      // Check AI scan complete
      if (evidence?.deep_scan_pages && Array.isArray(evidence.deep_scan_pages) && (evidence.deep_scan_pages as unknown[]).length > 0) {
        aiComplete++;
      }
    }
    
    return {
      pendingRender: flyerStats.pendingRender,
      rendered: flyerStats.rendered,
      ocrComplete,
      candidatesFound,
      aiComplete,
    };
  }, [items, flyerStats]);
  
  // CR075: Open render dialog for a specific flyer
  const openRenderDialog = (item: StagingDealItem) => {
    setRenderItem(item);
    setRenderDialogOpen(true);
  };
  
  // CR075: Handle render complete
  const handleRenderComplete = () => {
    setRenderDialogOpen(false);
    setRenderItem(null);
    fetchItems(); // Refresh the list
    toast({ title: "Render complete", description: "Flyer pages have been rendered. Run OCR + Candidate Scan next." });
  };

  // CR094: Separate deal items from flyer artifacts
  const flyerArtifacts = useMemo(() => items.filter(isFlyerArtifact), [items]);
  const dealItems = useMemo(() => items.filter(i => !isFlyerArtifact(i)), [items]);
  
  // CR094: Counts should reflect deal items only (not artifacts)
  const aiDealCount = dealItems.filter((i) => isAiExtractedDeal(i)).length;
  const websiteCount = dealItems.filter((i) => i.source_type === "WEBSITE_PROMPT_PACK").length;
  // Flyer deals are AI-extracted deals from flyer source (not artifacts)
  const flyerDealCount = dealItems.filter((i) => i.source_type === "FLYER" || isAiExtractedDeal(i)).length;
  
  // CR080: Check if flyers are in scope (for render stats badge, not actions panel)
  const hasFlyersInScope = sourcesEnabled.includes("FLYER") && flyerArtifacts.length > 0;
  
  // CR094: State for collapsed artifacts section
  const [artifactsExpanded, setArtifactsExpanded] = useState(false);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Unified Staging Deals</CardTitle>
              <CardDescription>
                Combined view of all validated deals from Flyers and Website sources
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {/* CR075: Bulk render button */}
              {pendingRenderFlyers.length > 0 && (
                <Button 
                  variant="default" 
                  size="sm" 
                  onClick={() => openRenderDialog(pendingRenderFlyers[0])}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Render Pending ({pendingRenderFlyers.length})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { fetchItems(); fetchGlobalStats(); }} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* CR111: Source summary badges - counts reflect GLOBAL filtered dataset, not current page */}
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="gap-1">
              Deals: {globalStats.totalDeals}
            </Badge>
            {sourceFilter === "all" && (
              <>
                {globalStats.aiDeals > 0 && (
                  <Badge variant="outline" className="gap-1 bg-purple-50 text-purple-700">
                    <Eye className="h-3 w-3" />
                    AI Deals: {globalStats.aiDeals}
                  </Badge>
                )}
                <Badge variant="outline" className="gap-1">
                  <Globe className="h-3 w-3" />
                  Website: {globalStats.websiteDeals}
                </Badge>
                {globalStats.flyerArtifacts > 0 && (
                  <Badge variant="outline" className="gap-1 text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    Flyer Artifacts: {globalStats.flyerArtifacts}
                  </Badge>
                )}
              </>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 flex items-center gap-2">
              <Input
                placeholder="Search merchant or item key..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="max-w-sm"
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {/* CR075: Attempt selector */}
            {attempts.length > 0 && (
              <Select value={selectedAttemptId} onValueChange={setSelectedAttemptId}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Select attempt" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Attempts</SelectItem>
                  {attempts.map((attempt, idx) => (
                    <SelectItem key={attempt.id} value={attempt.id}>
                      {attempt.step_key === "S1" ? "🗒️ Flyer: " : "🌐 Website: "}
                      {new Date(attempt.created_at).toLocaleString()} ({attempt.records_out ?? 0} deals)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="FLYER">Flyers Only</SelectItem>
                <SelectItem value="AI_DEALS">AI Extracted Deals</SelectItem>
                <SelectItem value="WEBSITE_PROMPT_PACK">Website Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {/* CR094: Deal Items Table - only shows actual deals, not flyer artifacts */}
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : dealItems.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {selectedAttemptId && selectedAttemptId !== "all" ? (
                <>
                  <p>No staged deals for this attempt.</p>
                  <p className="text-sm mt-1">
                    This attempt has 0 deals. Select "All Attempts" to view deals from prior runs, 
                    or re-run the Website Collect step.
                  </p>
                </>
              ) : flyerArtifacts.length > 0 ? (
                <>
                  <p>No AI-extracted deals yet.</p>
                  <p className="text-sm mt-1">Run the AI Deep Scan step to extract deals from flyer artifacts below.</p>
                </>
              ) : (
                <>
                  <p>No staging deals yet.</p>
                  <p className="text-sm mt-1">Execute the Flyers or Website Collect step to populate deals.</p>
                </>
              )}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Source</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="max-w-xs">Offer</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dealItems.map((item) => {
                    const sourceInfo = getSourceInfo(item);
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            {getSourceBadge(item)}
                            {sourceInfo.secondary && (
                              <span className="text-xs text-muted-foreground truncate max-w-24" title={sourceInfo.secondary}>
                                {sourceInfo.secondary}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {item.merchant_name || item.merchant_key}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {/* CR226: Use fallback helper with evidence_json support */}
                              {getDealTypeWithFallback(item.deal_type, item.validated_payload_json, item.evidence_json) || "-"}
                            </Badge>
                            {/* CR159+CR226: Variants indicator with evidence_json fallback */}
                            <VariantsBadge 
                              validatedPayloadJson={item.validated_payload_json} 
                              evidenceJson={item.evidence_json}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={item.offer_text || undefined}>
                          <div className="flex items-center gap-1">
                            {/* CR488: Show pre-normalize warning when DISCOUNT + gift card signals */}
                            {item.deal_type === "DISCOUNT" && hasGiftCardSignals(item) && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 bg-amber-100 text-amber-800 shrink-0">
                                Pre-S4
                              </Badge>
                            )}
                            <span className="truncate">
                              {/* CR226: Use fallback helper with evidence_json support */}
                              {/* CR488: Prefer evidence headline_mechanic when gift card signals + DISCOUNT */}
                              {(() => {
                                if (item.deal_type === "DISCOUNT" && hasGiftCardSignals(item)) {
                                  const ev = item.evidence_json as Record<string, unknown> || {};
                                  const rd = (ev.raw_deal || {}) as Record<string, unknown>;
                                  const hm = (rd?.headline_mechanic as string) || (rd?.headline as string);
                                  if (hm) return hm;
                                }
                                return getHeadlineWithFallback(item.offer_text, item.validated_payload_json, item.evidence_json) || "-";
                              })()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {getValueDisplay(item)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {/* CR226: Use fallback helper with evidence_json support */}
                          {getDatesWithFallback(item.validity_start_date, item.validity_end_date, item.validated_payload_json, item.evidence_json).valid_from || "-"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {/* CR226: Use fallback helper with evidence_json support */}
                          {getDatesWithFallback(item.validity_start_date, item.validity_end_date, item.validated_payload_json, item.evidence_json).valid_until || "-"}
                        </TableCell>
                        <TableCell>
                          {/* CR094: Show appropriate status based on extraction method */}
                          {getExtractionStatusBadge(item)}
                        </TableCell>
                        <TableCell className="text-right flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedItem(item);
                              setDetailTab("deal");
                            }}
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* CR094: Collapsible Flyer Artifacts Section */}
          {flyerArtifacts.length > 0 && (
            <Collapsible 
              open={artifactsExpanded} 
              onOpenChange={setArtifactsExpanded}
              className="mt-4"
            >
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between" size="sm">
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Flyer Source Artifacts ({flyerArtifacts.length})
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${artifactsExpanded ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Merchant</TableHead>
                        <TableHead>Filename</TableHead>
                        <TableHead>Render Status</TableHead>
                        <TableHead>Page Count</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {flyerArtifacts.map((item) => {
                        const renderStatus = getFlyerRenderStatus(item);
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.merchant_key}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {item.evidence_json?.flyer_filename || item.evidence_json?.flyer_file_id || "-"}
                            </TableCell>
                            <TableCell>{getExtractionStatusBadge(item)}</TableCell>
                            <TableCell>
                              {item.evidence_json?.page_count || item.evidence_json?.flyer_pages?.length || "-"}
                            </TableCell>
                            <TableCell className="text-right flex items-center gap-1 justify-end">
                              {renderStatus.canRender && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRenderDialog(item)}
                                  title="Render pages"
                                >
                                  <ImageIcon className="h-3 w-3 mr-1" />
                                  Render
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setDetailTab("evidence");
                                }}
                                title="View evidence"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog with Tabs */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem && getSourceBadge(selectedItem)}
              {selectedItem?.merchant_key}
            </DialogTitle>
            <DialogDescription>
              {getDealType(selectedItem?.validated_payload_json)} Deal
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (() => {
            // CR: Unwrap validated deal and validation meta once for the dialog
            const deal = getValidatedDeal(selectedItem.validated_payload_json);
            const validationMeta = getValidationMeta(selectedItem.validated_payload_json);
            const dates = getDates(selectedItem.validated_payload_json);
            
            return (
            <Tabs value={detailTab} onValueChange={setDetailTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="deal">Deal</TabsTrigger>
                <TabsTrigger value="validation">Validation</TabsTrigger>
                <TabsTrigger value="evidence">Evidence</TabsTrigger>
                <TabsTrigger value="provenance">Provenance</TabsTrigger>
              </TabsList>

              <ScrollArea className="max-h-[60vh] mt-4">
                <TabsContent value="deal" className="mt-0">
                  <div className="space-y-4 pr-4">
                    {/* Deal Summary */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="text-sm text-muted-foreground">Type</div>
                        <div className="font-medium">
                          {deal.deal_type || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Brand</div>
                        <div className="font-medium">
                          {getPurchaseBrand(selectedItem.validated_payload_json) || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Confidence</div>
                        <div>
                          {getConfidenceBadge(deal.confidence)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Valid Until</div>
                        <div className="font-medium">
                          {dates.valid_until || "-"}
                        </div>
                      </div>
                    </div>

                    {/* CR159: Full Variants section using VariantsDetailList */}
                    <VariantsDetailList validatedPayloadJson={selectedItem.validated_payload_json} />

                    {/* Offer Text */}
                    <div>
                      <h4 className="font-medium mb-2">Offer Text</h4>
                      <div className="p-3 bg-muted/50 rounded-lg text-sm">
                        {deal.headline_mechanic ||
                          deal.raw_offer_text ||
                          "No offer text available"}
                      </div>
                    </div>

                    {/* Full Extracted Deal JSON */}
                    <div>
                      <h4 className="font-medium mb-2">Extracted Deal (Full)</h4>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[300px]">
                        {JSON.stringify(deal, null, 2)}
                      </pre>
                    </div>

                    {/* Links */}
                    {(selectedItem.deal_link || selectedItem.detail_link || selectedItem.source_url_used) && (
                      <div>
                        <h4 className="font-medium mb-2">Links</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedItem.source_url_used && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(selectedItem.source_url_used!, "_blank")}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Source URL
                            </Button>
                          )}
                          {selectedItem.deal_link && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(selectedItem.deal_link!, "_blank")}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Deal Link
                            </Button>
                          )}
                          {selectedItem.detail_link && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(selectedItem.detail_link!, "_blank")}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Detail Link
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="validation" className="mt-0">
                  <div className="space-y-4 pr-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="text-sm text-muted-foreground">Publishable</div>
                        <div>
                          {validationMeta.is_publishable ? (
                            <Badge className="bg-green-500">Yes</Badge>
                          ) : (
                            <Badge variant="destructive">No</Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Missing Fields</div>
                        <div className="font-medium">
                          {validationMeta.missing_fields?.length || 0}
                        </div>
                      </div>
                    </div>

                    {validationMeta.missing_fields?.length ? (
                      <div>
                        <h4 className="font-medium mb-2">Missing Fields</h4>
                        <div className="flex flex-wrap gap-1">
                          {validationMeta.missing_fields.map((f) => (
                            <Badge key={f} variant="outline">{f}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {validationMeta.warnings?.length ? (
                      <div>
                        <h4 className="font-medium mb-2">Warnings</h4>
                        <ul className="list-disc list-inside text-sm text-muted-foreground">
                          {validationMeta.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    <div>
                      <h4 className="font-medium mb-2">Full Validation Meta</h4>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto">
                        {JSON.stringify(validationMeta, null, 2)}
                      </pre>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="evidence" className="mt-0">
                  <div className="space-y-4 pr-4">
                    {/* Evidence Links */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedItem.evidence_json.flyer_file_id && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <FileText className="h-4 w-4" />
                            Flyer File
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {selectedItem.evidence_json.flyer_file_id}
                          </code>
                          {selectedItem.evidence_json.flyer_page_numbers && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Pages: {selectedItem.evidence_json.flyer_page_numbers.join(", ")}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedItem.evidence_json.page_image_url && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <Image className="h-4 w-4" />
                            Page Image
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(selectedItem.evidence_json.page_image_url!, "_blank")}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </div>
                      )}

                      {selectedItem.evidence_json.listing_html_artifact_id && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <Globe className="h-4 w-4" />
                            Listing HTML
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {selectedItem.evidence_json.listing_html_artifact_id}
                          </code>
                        </div>
                      )}

                      {selectedItem.evidence_json.detail_html_artifact_id && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <Globe className="h-4 w-4" />
                            Detail HTML
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {selectedItem.evidence_json.detail_html_artifact_id}
                          </code>
                        </div>
                      )}

                      {selectedItem.evidence_json.validate_output_artifact_id && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <FileJson className="h-4 w-4" />
                            Validate Output
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {selectedItem.evidence_json.validate_output_artifact_id}
                          </code>
                        </div>
                      )}

                      {selectedItem.evidence_json.poc_run_id && (
                        <div className="p-3 border rounded-lg">
                          <div className="flex items-center gap-2 text-sm font-medium mb-1">
                            <FileJson className="h-4 w-4" />
                            POC Run
                          </div>
                          <code className="text-xs text-muted-foreground">
                            {selectedItem.evidence_json.poc_run_id}
                          </code>
                        </div>
                      )}
                    </div>

                    {/* Snippets */}
                    {selectedItem.evidence_json.listing_snippet && (
                      <div>
                        <h4 className="font-medium mb-2">Listing Snippet</h4>
                        <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[200px] whitespace-pre-wrap">
                          {selectedItem.evidence_json.listing_snippet}
                        </pre>
                      </div>
                    )}

                    {selectedItem.evidence_json.detail_snippet && (
                      <div>
                        <h4 className="font-medium mb-2">Detail Snippet</h4>
                        <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[200px] whitespace-pre-wrap">
                          {selectedItem.evidence_json.detail_snippet}
                        </pre>
                      </div>
                    )}

                    {/* Full Evidence JSON */}
                    <div>
                      <h4 className="font-medium mb-2">Full Evidence JSON</h4>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[200px]">
                        {JSON.stringify(selectedItem.evidence_json, null, 2)}
                      </pre>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="provenance" className="mt-0">
                  <div className="space-y-4 pr-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="text-sm text-muted-foreground">Source Type</div>
                        <div className="font-medium">{selectedItem.source_type}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Merchant Source ID</div>
                        <div className="font-medium truncate">
                          {selectedItem.provenance_json.merchant_source_id || "-"}
                        </div>
                      </div>
                      {selectedItem.provenance_json.prompt_pack_id && (
                        <>
                          <div>
                            <div className="text-sm text-muted-foreground">Prompt Pack</div>
                            <code className="text-xs">{selectedItem.provenance_json.prompt_pack_id}</code>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground">Execution Order</div>
                            <div className="font-medium">
                              {selectedItem.provenance_json.execution_order ?? "-"}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Item Key</h4>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {selectedItem.staging_item_key}
                      </code>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2">Full Provenance JSON</h4>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto">
                        {JSON.stringify(selectedItem.provenance_json, null, 2)}
                      </pre>
                    </div>
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          );
          })()}
        </DialogContent>
      </Dialog>
      
      {/* CR075: Flyer Render Dialog */}
      {renderItem && (
        <FlyerRenderDialog
          open={renderDialogOpen}
          onOpenChange={setRenderDialogOpen}
          stagingItem={{
            id: renderItem.id,
            batch_run_id: renderItem.batch_run_id,
            merchant_key: renderItem.merchant_key,
            evidence_json: renderItem.evidence_json,
          }}
          onComplete={handleRenderComplete}
        />
      )}
    </div>
  );
}
